import { Routes, Route, Navigate } from 'react-router-dom';
import { Suspense, lazy, useEffect, useState, type ReactNode } from 'react';
import { ErrorBoundary } from './components/ErrorBoundary';
import { Toaster } from './components/ui/toaster';
import { ConfirmDialog } from './components/shared/ConfirmDialog';
import { ScrollToTop } from './components/shared/ScrollToTop';
import { CookieConsent } from './components/shared/CookieConsent';

// Landing page loaded eagerly (it's the entry point)
import LandingPage from './routes/LandingPage';

// Public pages (lazy)
const PricingPage = lazy(() => import('./routes/PricingPage'));
const StatusPage = lazy(() => import('./routes/StatusPage'));
const IntegrationsPublicPage = lazy(() => import('./routes/IntegrationsPublicPage'));
const ToolsPublicPage = lazy(() => import('./routes/ToolsPublicPage'));
const ChangelogPage = lazy(() => import('./routes/ChangelogPage'));
const BlogPage = lazy(() => import('./routes/BlogPage'));
const BlogPostPage = lazy(() => import('./routes/BlogPostPage'));
const ApiDocsPage = lazy(() => import('./routes/ApiDocsPage'));
const InviteAcceptPage = lazy(() => import('./routes/InviteAcceptPage'));

// Legal pages (lazy)
const PrivacyPolicyPage = lazy(() => import('./routes/legal/PrivacyPolicyPage'));
const TermsOfServicePage = lazy(() => import('./routes/legal/TermsOfServicePage'));
const RefundPolicyPage = lazy(() => import('./routes/legal/RefundPolicyPage'));
const SubprocessorsPage = lazy(() => import('./routes/legal/SubprocessorsPage'));
const ContactPage = lazy(() => import('./routes/legal/ContactPage'));

// Auth pages (lazy)
const LoginPage = lazy(() => import('./routes/auth/LoginPage'));
const RegisterPage = lazy(() => import('./routes/auth/RegisterPage'));
const AuthCallbackPage = lazy(() => import('./routes/auth/AuthCallbackPage'));
const ForgotPasswordPage = lazy(() => import('./routes/auth/ForgotPasswordPage'));
const ResetPasswordPage = lazy(() => import('./routes/auth/ResetPasswordPage'));
const CompleteProfilePage = lazy(() => import('./routes/auth/CompleteProfilePage'));

// Docs pages (lazy)
const DocsLayout = lazy(() => import('./routes/docs/DocsLayout'));
const GettingStartedPage = lazy(() => import('./routes/docs/GettingStartedPage'));
const CliReferencePage = lazy(() => import('./routes/docs/CliReferencePage'));
const EndpointsDocPage = lazy(() => import('./routes/docs/EndpointsDocPage'));
const TunnelsDocPage = lazy(() => import('./routes/docs/TunnelsDocPage'));
const LogsDocPage = lazy(() => import('./routes/docs/LogsDocPage'));
const SecurityDocPage = lazy(() => import('./routes/docs/SecurityDocPage'));
const LimitsDocPage = lazy(() => import('./routes/docs/LimitsDocPage'));
const WorkspacesDocPage = lazy(() => import('./routes/docs/WorkspacesDocPage'));
const ReplayDocPage = lazy(() => import('./routes/docs/ReplayDocPage'));
const IntegrationsDocPage = lazy(() => import('./routes/docs/IntegrationsDocPage'));
const TroubleshootingDocPage = lazy(() => import('./routes/docs/TroubleshootingDocPage'));
const GlossaryPage = lazy(() => import('./routes/docs/GlossaryPage'));
const TrialDocPage = lazy(() => import('./routes/docs/TrialDocPage'));

// Dashboard pages (lazy)
const DashboardLayout = lazy(() => import('./routes/dashboard/DashboardLayout'));
const OverviewPage = lazy(() => import('./routes/dashboard/OverviewPage'));
const AnalyticsPage = lazy(() => import('./routes/dashboard/AnalyticsPage'));
const EndpointsPage = lazy(() => import('./routes/dashboard/EndpointsPage'));
const TunnelsPage = lazy(() => import('./routes/dashboard/TunnelsPage'));
const LogsPage = lazy(() => import('./routes/dashboard/LogsPage'));
const ReplayHistoryPage = lazy(() => import('./routes/dashboard/ReplayHistoryPage'));
const FailedWebhooksPage = lazy(() => import('./routes/dashboard/FailedWebhooksPage'));
const ApiKeysPage = lazy(() => import('./routes/dashboard/ApiKeysPage'));
const SettingsPage = lazy(() => import('./routes/dashboard/SettingsPage'));
const IntegrationsPage = lazy(() => import('./routes/dashboard/IntegrationsPage'));
const ToolsPage = lazy(() => import('./routes/dashboard/ToolsPage'));
const FeedbackPage = lazy(() => import('./routes/dashboard/FeedbackPage'));
const WorkspaceSettingsPage = lazy(() => import('./routes/dashboard/WorkspaceSettingsPage'));

// Admin pages (lazy)
const AdminGuard = lazy(() => import('./components/AdminGuard').then(m => ({ default: m.AdminGuard })));
const AdminLayout = lazy(() => import('./routes/admin/AdminLayout'));
const AdminOverviewPage = lazy(() => import('./routes/admin/AdminOverviewPage'));
const AdminUsersPage = lazy(() => import('./routes/admin/AdminUsersPage'));
const AdminUserDetailPage = lazy(() => import('./routes/admin/AdminUserDetailPage'));
const AdminLogsPage = lazy(() => import('./routes/admin/AdminLogsPage'));
const AdminPaymentsPage = lazy(() => import('./routes/admin/AdminPaymentsPage'));
const AdminPlansPage = lazy(() => import('./routes/admin/AdminPlansPage'));
const AdminIncidentsPage = lazy(() => import('./routes/admin/AdminIncidentsPage'));
const AdminSystemLogsPage = lazy(() => import('./routes/admin/AdminSystemLogsPage'));
const AdminAuditLogsPage = lazy(() => import('./routes/admin/AdminAuditLogsPage'));
const AdminSettingsPage = lazy(() => import('./routes/admin/AdminSettingsPage'));
const AdminInboxPage = lazy(() => import('./routes/admin/AdminInboxPage'));
const AdminEmailTemplatesPage = lazy(() => import('./routes/admin/AdminEmailTemplatesPage'));
const AdminAnalyticsPage = lazy(() => import('./routes/admin/AdminAnalyticsPage'));
const AdminFeedbackPage = lazy(() => import('./routes/admin/AdminFeedbackPage'));
const AdminBackupsPage = lazy(() => import('./routes/admin/AdminBackupsPage'));
const AdminChangelogPage = lazy(() => import('./routes/admin/AdminChangelogPage'));
const AdminBlogPage = lazy(() => import('./routes/admin/AdminBlogPage'));
const AdminBlogEditorPage = lazy(() => import('./routes/admin/AdminBlogEditorPage'));
const AdminWorkspacesPage = lazy(() => import('./routes/admin/AdminWorkspacesPage'));
const AdminWorkspaceDetailPage = lazy(() => import('./routes/admin/AdminWorkspaceDetailPage'));
const AdminFailedWebhooksPage = lazy(() => import('./routes/admin/AdminFailedWebhooksPage'));
const AdminEndpointsPage = lazy(() => import('./routes/admin/AdminEndpointsPage'));

function PageLoader() {
  return (
    <div className="flex h-screen items-center justify-center">
      <div className="text-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent mx-auto mb-4" />
      </div>
    </div>
  );
}

/**
 * Wrapper that waits for Linabase auth before rendering protected routes.
 * Lazy-imports linabase so the vendor chunk stays out of the initial bundle.
 */
function AuthGate({ children }: { children: (user: import('@linabase/js').AuthUser | null) => ReactNode }) {
  const [user, setUser] = useState<import('@linabase/js').AuthUser | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    import('./lib/linabase').then(({ linabase }) => {
      const subscription = linabase.auth.onAuthStateChange((event, session) => {
        if (cancelled) return;
        setUser(session?.user ?? null);
        if (event === 'INITIAL_SESSION') {
          setLoading(false);
        }
      });
      // Store unsubscribe for cleanup
      if (!cancelled) {
        cleanupRef.current = () => subscription.unsubscribe();
      } else {
        subscription.unsubscribe();
      }
    });
    const cleanupRef = { current: () => {} };
    return () => {
      cancelled = true;
      cleanupRef.current();
    };
  }, []);

  if (loading) {
    return <PageLoader />;
  }

  return <>{children(user)}</>;
}

function App() {
  return (
    <ErrorBoundary>
      <ScrollToTop />
      <Suspense fallback={<PageLoader />}>
        <Routes>
          {/* Public routes — render immediately, no auth check */}
          <Route path="/" element={<LandingPage />} />
          <Route path="/pricing" element={<PricingPage />} />
          <Route path="/status" element={<StatusPage />} />
          <Route path="/integrations" element={<IntegrationsPublicPage />} />
          <Route path="/tools" element={<ToolsPublicPage />} />
          <Route path="/changelog" element={<ChangelogPage />} />
          <Route path="/blog" element={<BlogPage />} />
          <Route path="/blog/:slug" element={<BlogPostPage />} />
          <Route path="/privacy" element={<PrivacyPolicyPage />} />
          <Route path="/terms" element={<TermsOfServicePage />} />
          <Route path="/refunds" element={<RefundPolicyPage />} />
          <Route path="/subprocessors" element={<SubprocessorsPage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/docs/api" element={<ApiDocsPage />} />
          <Route path="/docs" element={<DocsLayout />}>
            <Route index element={null} />
            <Route path="getting-started" element={<GettingStartedPage />} />
            <Route path="trial" element={<TrialDocPage />} />
            <Route path="cli" element={<CliReferencePage />} />
            <Route path="endpoints" element={<EndpointsDocPage />} />
            <Route path="tunnels" element={<TunnelsDocPage />} />
            <Route path="logs" element={<LogsDocPage />} />
            <Route path="security" element={<SecurityDocPage />} />
            <Route path="limits" element={<LimitsDocPage />} />
            <Route path="workspaces" element={<WorkspacesDocPage />} />
            <Route path="replay" element={<ReplayDocPage />} />
            <Route path="integrations" element={<IntegrationsDocPage />} />
            <Route path="troubleshooting" element={<TroubleshootingDocPage />} />
            <Route path="glossary" element={<GlossaryPage />} />
          </Route>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/auth/callback" element={<AuthCallbackPage />} />
          <Route path="/auth/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/auth/reset-password" element={<ResetPasswordPage />} />

          {/* Protected routes — wait for auth */}
          <Route
            path="/auth/complete-profile"
            element={<AuthGate>{(user) => user ? <CompleteProfilePage /> : <Navigate to="/login" />}</AuthGate>}
          />
          <Route
            path="/dashboard"
            element={<AuthGate>{(user) => user ? <DashboardLayout /> : <Navigate to="/login" />}</AuthGate>}
          >
            <Route index element={<OverviewPage />} />
            <Route path="analytics" element={<AnalyticsPage />} />
            <Route path="endpoints" element={<EndpointsPage />} />
            <Route path="integrations" element={<IntegrationsPage />} />
            <Route path="tools" element={<ToolsPage />} />
            <Route path="tunnels" element={<TunnelsPage />} />
            <Route path="logs" element={<LogsPage />} />
            <Route path="replays" element={<ReplayHistoryPage />} />
            <Route path="failed-webhooks" element={<FailedWebhooksPage />} />
            <Route path="keys" element={<ApiKeysPage />} />
            <Route path="feedback" element={<FeedbackPage />} />
            <Route path="settings" element={<SettingsPage />} />
            <Route path="workspace/:workspaceId/settings" element={<WorkspaceSettingsPage />} />
          </Route>
          <Route
            path="/invite/:token"
            element={<AuthGate>{(user) => user ? <InviteAcceptPage /> : <Navigate to="/login" />}</AuthGate>}
          />
          <Route
            path="/admin"
            element={<AuthGate>{(user) => user ? <Suspense fallback={<PageLoader />}><AdminGuard><AdminLayout /></AdminGuard></Suspense> : <Navigate to="/login" />}</AuthGate>}
          >
            <Route index element={<AdminOverviewPage />} />
            <Route path="analytics" element={<AdminAnalyticsPage />} />
            <Route path="users" element={<AdminUsersPage />} />
            <Route path="users/:id" element={<AdminUserDetailPage />} />
            <Route path="payments" element={<AdminPaymentsPage />} />
            <Route path="plans" element={<AdminPlansPage />} />
            <Route path="incidents" element={<AdminIncidentsPage />} />
            <Route path="logs" element={<AdminLogsPage />} />
            <Route path="system-logs" element={<AdminSystemLogsPage />} />
            <Route path="audit-logs" element={<AdminAuditLogsPage />} />
            <Route path="settings" element={<AdminSettingsPage />} />
            <Route path="inbox" element={<AdminInboxPage />} />
            <Route path="email-templates" element={<AdminEmailTemplatesPage />} />
            <Route path="feedback" element={<AdminFeedbackPage />} />
            <Route path="backups" element={<AdminBackupsPage />} />
            <Route path="changelog" element={<AdminChangelogPage />} />
            <Route path="blog" element={<AdminBlogPage />} />
            <Route path="blog/:id" element={<AdminBlogEditorPage />} />
            <Route path="workspaces" element={<AdminWorkspacesPage />} />
            <Route path="workspaces/:id" element={<AdminWorkspaceDetailPage />} />
            <Route path="failed-webhooks" element={<AdminFailedWebhooksPage />} />
            <Route path="endpoints" element={<AdminEndpointsPage />} />
          </Route>

          {/* Catch all */}
          <Route path="*" element={<Navigate to="/" />} />
        </Routes>
      </Suspense>
      <Toaster />
      <ConfirmDialog />
      <CookieConsent />
    </ErrorBoundary>
  );
}

export default App;
